import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  TooltipContextProvider,
  TooltipProviderContextProvider
} from './tooltip-context'
import { v4 as uuid } from 'uuid'
import { StyledTooltipRoot } from '../styled-components'
import PropTypes from 'prop-types'

const TooltipRoot = ({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}) => {
  const [trigger, setTrigger] = useState(null)
  const contentId = uuid()
  const openTimerRef = useRef(0)

  const providerContext = useContext(TooltipProviderContextProvider)

  const delayDuration = providerContext.delayDuration
  const wasOpenDelayedRef = useRef(false)
  const [componentOpen = false, setComponentOpen] = useState(defaultOpen)

  const open = openProp ?? componentOpen
  const setOpen = onOpenChange ?? setComponentOpen

  const stateAttribute = useMemo(() => {
    return open
      ? wasOpenDelayedRef.current
        ? 'delayed-open'
        : 'instant-open'
      : 'closed'
  }, [open])

  const handleOpen = useCallback(() => {
    window.clearTimeout(openTimerRef.current)
    wasOpenDelayedRef.current = false
    setOpen(true)
  }, [setOpen])

  const handleClose = useCallback(() => {
    window.clearTimeout(openTimerRef.current)
    setOpen(false)
  }, [setOpen])

  const handleDelayedOpen = useCallback(() => {
    window.clearTimeout(openTimerRef.current)
    openTimerRef.current = window.setTimeout(() => {
      wasOpenDelayedRef.current = true
      setOpen(true)
    }, delayDuration)
  }, [delayDuration, setOpen])

  useEffect(() => {
    return () => window.clearTimeout(openTimerRef.current)
  }, [])

  const onTriggerEnter = useCallback(() => {
    if (providerContext.isOpenDelayed) handleDelayedOpen()
    else handleOpen()
  }, [providerContext.isOpenDelayed, handleDelayedOpen, handleOpen])

  const onTriggerLeave = useCallback(() => {
    // Clear the timer in case the pointer leaves the trigger before the tooltip is opened.
    window.clearTimeout(openTimerRef.current)
  }, [handleClose])

  const contextValue = {
    contentId,
    open,
    stateAttribute,
    trigger,
    onTriggerEnter,
    onTriggerLeave,
    onTriggerChange: setTrigger,
    onOpen: handleOpen,
    onClose: handleClose
  }

  return (
    <StyledTooltipRoot {...props}>
      <TooltipContextProvider.Provider value={contextValue}>
        {children}
      </TooltipContextProvider.Provider>
    </StyledTooltipRoot>
  )
}

TooltipRoot.propType = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func
}

export default TooltipRoot
